import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SnackBarService } from 'app/services/snack-bar/snack-bar.service';
import { getPatch } from 'app/utils/json-patch';
import { CommandResult } from 'app/utils/network';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { ProjectStatusActions, ProjectStatusReport, ProjectStatusSelectors } from '.';
import { AppStore } from '..';
import { RestApiService } from '../../services/rest-api';
import { Project, ProjectSelectors } from '../project';
import { okEmptyAction } from '../project/project.actions';
import { Model } from '../shared/models/base.model';
import { EntityPatchSuccessResponse } from './../../services/rest-api/features/patch.response';

@Injectable()
export class ProjectStatusEffects {
    loadProjectStatusReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectStatusActions.loadProjectStatuses),
            mergeMap((action) =>
                this.restApiService.process(action).pipe(
                    map((response: CommandResult) =>
                        ProjectStatusActions.loadProjectStatusesSuccess({
                            statuses: response.payload.map((dto) =>
                                Model.createFromDto(ProjectStatusReport, dto)
                            ),
                            projectId: action.payload.projectId,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ProjectStatusActions.loadProjectStatusesFail({
                                error,
                                projectId: action.payload.projectId,
                            })
                        )
                    )
                )
            )
        )
    );

    loadProjectStatusesBySiteId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectStatusActions.loadProjectStatusesBySiteId),
            mergeMap((action) =>
                this.restApiService.process(action).pipe(
                    withLatestFrom(this.store$.select(ProjectSelectors.selectCurrentSiteProjects)),
                    map(([response, projects]: [CommandResult, Project[]]) =>
                        ProjectStatusActions.loadProjectStatusesBySiteIdSuccess({
                            statuses: response.payload.map((dto) =>
                                Model.createFromDto(ProjectStatusReport, dto)
                            ),
                            siteId: action.payload.siteId,
                            projectIds: projects.map((p) => p.id),
                        })
                    ),
                    catchError((error) =>
                        of(
                            ProjectStatusActions.loadProjectStatusesBySiteIdFail({
                                error,
                                siteId: action.payload.siteId,
                            })
                        )
                    )
                )
            )
        )
    );

    addProjectStatusReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectStatusActions.addProjectStatus),
            mergeMap((action) => {
                if (action.showSnackBar) {
                    this.snackBarService.showInfiniteBar('Create a new report...');
                }
                return this.restApiService.process(action).pipe(
                    tap(() => this.snackBarService.hide()),
                    map((response: CommandResult) =>
                        ProjectStatusActions.addProjectStatusSuccess({
                            report: Model.createFromDto(ProjectStatusReport, response.payload),
                        })
                    ),
                    catchError((error) => {
                        this.snackBarService.hide();
                        return of(ProjectStatusActions.addProjectStatusFail({ error }));
                    })
                );
            })
        )
    );

    updatepProjectStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectStatusActions.updateProjectStatus),
            withLatestFrom(this.store$.select(ProjectStatusSelectors.selectAll)),
            mergeMap(([action, reports]) => {
                const report = reports.find((r) => r.id === action.payload.id);
                const patch = getPatch(report, action.payload.changes);
                return of(
                    ProjectStatusActions.patchProjectStatus({
                        payload: { id: action.payload.id, patch },
                        options: action.options,
                    })
                );
            })
        )
    );

    patchProjectStatusReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectStatusActions.patchProjectStatus),
            concatMap((action) =>
                this.restApiService.process(action).pipe(
                    map((response: EntityPatchSuccessResponse) => {
                        if (action.options?.optimistic) return okEmptyAction();
                        console.log(response);
                        return ProjectStatusActions.patchProjectStatusSuccess({
                            id: action.payload.id,
                            patch: response.payload,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProjectStatusActions.patchProjectStatusFail({
                                payload: { error },
                                originAction: action,
                            })
                        )
                    )
                )
            )
        )
    );

    deleteProjectStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectStatusActions.deleteProjectStatus),
            mergeMap((action) =>
                this.restApiService.process(action).pipe(
                    map(() => {
                        if (action.options?.optimistic) return okEmptyAction();
                        return ProjectStatusActions.deleteProjectStatusSuccess({
                            id: action.payload.id,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProjectStatusActions.deleteProjectStatusFail({
                                payload: { error },
                                originAction: action,
                            })
                        )
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private restApiService: RestApiService,
        private store$: Store<AppStore.AppState>,
        private snackBarService: SnackBarService
    ) {}
}
